import {
    createAssetPartnerIcProfile,
    deleteAssetPartnerIcProfile,
    fetchAssetPartnerIcProfiles,
    fetchD2GVersions,
    fetchMFHInvestmentCases,
    fetchMFHInvestmentCaseVersions,
    getApprovedMFHInvestmentCase,
    getMFHInvestmentCase,
    getMFHInvestmentCaseVersionInputPreview,
    getMFHInvestmentCaseVersionResultPreview,
    getMFHInvestmentCaseVersionSheetPreview,
    updateAssetPartnerIcProfile,
} from '@/api/mfh';
import type { IMFHAssetPartnerIcProfile, MFHInvestmentCase } from '@condo/domain';
import type { MFHInvestmentCaseType } from '@condo/domain';
import type { UseQueryOptions } from '@tanstack/vue-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type {
    MFHInvestmentCaseVersionInputPreviewResponse,
    MFHInvestmentCaseVersionResultPreviewResponse,
    MFHInvestmentCaseVersionSheetPreviewResponse,
    MFHInvestmentCaseVersionsResponse,
} from '@/api/mfh/mfh-investment-case.api';
import { type MaybeRef, computed, unref } from 'vue';

export const useMFHInvestmentCases = (
    mfhId: MaybeRef<number>,
    investmentCaseType?: MaybeRef<MFHInvestmentCaseType | undefined>,
    options?: MaybeRef<{ includeDeletedInvestmentCases?: boolean } | undefined>,
) => {
    const queryKey = computed(() => ['mfh-investment-case', unref(mfhId), unref(investmentCaseType), unref(options)?.includeDeletedInvestmentCases]);
    return useQuery(
        queryKey,
        async (): Promise<MFHInvestmentCase[]> =>
            fetchMFHInvestmentCases({
                mfhId: unref(mfhId),
                investmentCaseType: unref(investmentCaseType),
                includeDeletedInvestmentCases: unref(options)?.includeDeletedInvestmentCases,
            }),
        {
            initialData: [],
        },
    );
};

export const useMFHInvestmentCase = (
    params: MaybeRef<{ mfhId: number; mfhInvestmentCaseId: number }>,
    options?: Omit<UseQueryOptions<MFHInvestmentCase>, 'queryKey' | 'queryFn'>,
) => {
    const queryKey = computed(() => {
        const { mfhId, ...paramVals } = unref(params);
        return ['mfh-investment-case', mfhId, paramVals];
    });
    return useQuery(queryKey, async (): Promise<MFHInvestmentCase> => getMFHInvestmentCase(unref(params)), options);
};

export const useApprovedMFHInvestmentCase = (mfhId: MaybeRef<number>, options?: Omit<UseQueryOptions<MFHInvestmentCase | null>, 'queryKey' | 'queryFn'>) => {
    const queryKey = computed(() => ['mfh-investment-case-approved', unref(mfhId)]);
    return useQuery(queryKey, async (): Promise<MFHInvestmentCase | null> => getApprovedMFHInvestmentCase({ mfhId: unref(mfhId) }), options);
};

export const useD2GVersions = () => useQuery(['mfh-investment-case', 'd2g-versions'], fetchD2GVersions);

export const useMFHInvestmentCaseVersions = (mfhId: MaybeRef<number>, mfhInvestmentCaseId: MaybeRef<number | undefined>, enabled: MaybeRef<boolean> = true) => {
    const queryKey = computed(() => ['mfh-investment-case', unref(mfhId), unref(mfhInvestmentCaseId), 'versions']);
    return useQuery(
        queryKey,
        async (): Promise<MFHInvestmentCaseVersionsResponse> =>
            fetchMFHInvestmentCaseVersions({
                mfhId: unref(mfhId),
                mfhInvestmentCaseId: unref(mfhInvestmentCaseId)!,
            }),
        {
            enabled: computed(() => unref(enabled) && !!unref(mfhId) && !!unref(mfhInvestmentCaseId)),
        },
    );
};

type MFHInvestmentCaseVersionPreviewParams = {
    mfhId: number;
    mfhInvestmentCaseId: number;
    mfhInvestmentCaseVersionId: number;
};

const isVersionPreviewEnabled = (params: MaybeRef<MFHInvestmentCaseVersionPreviewParams>, enabled: MaybeRef<boolean>) => {
    const values = unref(params);
    return unref(enabled) && !!values.mfhId && !!values.mfhInvestmentCaseId && !!values.mfhInvestmentCaseVersionId;
};

export const useMFHInvestmentCaseVersionInputPreview = (params: MaybeRef<MFHInvestmentCaseVersionPreviewParams>, enabled: MaybeRef<boolean> = false) =>
    useQuery(
        computed(() => {
            const values = unref(params);
            return ['mfh-investment-case', values.mfhId, values.mfhInvestmentCaseId, 'version-input-preview', values.mfhInvestmentCaseVersionId];
        }),
        async (): Promise<MFHInvestmentCaseVersionInputPreviewResponse> => getMFHInvestmentCaseVersionInputPreview(unref(params)),
        { enabled: computed(() => isVersionPreviewEnabled(params, enabled)) },
    );

export const useMFHInvestmentCaseVersionResultPreview = (params: MaybeRef<MFHInvestmentCaseVersionPreviewParams>, enabled: MaybeRef<boolean> = false) =>
    useQuery(
        computed(() => {
            const values = unref(params);
            return ['mfh-investment-case', values.mfhId, values.mfhInvestmentCaseId, 'version-result-preview', values.mfhInvestmentCaseVersionId];
        }),
        async (): Promise<MFHInvestmentCaseVersionResultPreviewResponse> => getMFHInvestmentCaseVersionResultPreview(unref(params)),
        { enabled: computed(() => isVersionPreviewEnabled(params, enabled)) },
    );

export const useMFHInvestmentCaseVersionSheetPreview = (params: MaybeRef<MFHInvestmentCaseVersionPreviewParams>, enabled: MaybeRef<boolean> = false) =>
    useQuery(
        computed(() => {
            const values = unref(params);
            return ['mfh-investment-case', values.mfhId, values.mfhInvestmentCaseId, 'version-sheet-preview', values.mfhInvestmentCaseVersionId];
        }),
        async (): Promise<MFHInvestmentCaseVersionSheetPreviewResponse> => getMFHInvestmentCaseVersionSheetPreview(unref(params)),
        { enabled: computed(() => isVersionPreviewEnabled(params, enabled)) },
    );

export const useAssetPartnerIcProfiles = (params?: MaybeRef<{ companyId?: number }>) => {
    const queryKey = computed(() => ['asset-partner-ic-profiles', unref(params)]);
    return useQuery(queryKey, async (): Promise<IMFHAssetPartnerIcProfile[]> => fetchAssetPartnerIcProfiles(unref(params)), {
        enabled: computed(() => params === undefined || !!unref(params)?.companyId),
    });
};

export const useCreateAssetPartnerIcProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createAssetPartnerIcProfile,
        onSuccess: () => {
            queryClient.invalidateQueries(['asset-partner-ic-profiles']);
        },
    });
};

export const useUpdateAssetPartnerIcProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateAssetPartnerIcProfile,
        onSuccess: () => {
            queryClient.invalidateQueries(['asset-partner-ic-profiles']);
        },
    });
};

export const useDeleteAssetPartnerIcProfile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteAssetPartnerIcProfile,
        onSuccess: () => {
            queryClient.invalidateQueries(['asset-partner-ic-profiles']);
        },
    });
};
